import {
  CheckBoxField,
  DrawerComponent,
  DrawerMeta,
  Form,
  RadioField,
  RenderFuncProps,
  Section,
  SectionDivider,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
  TaskTemplateForm,
  TaskTemplateDetailProps,
  Duration,
} from '@dispatch/shared/types';
import { AttributesField } from './components/AttributesField';
import { ResourcePriority } from './components/ResourcePriority';
import { RoutingRulesField } from './components/RoutingRules';
import { TaskDuration } from './components/TaskDuration';
import _ from 'lodash';
import React from 'react';

declare module 'yup' {
  // tslint:disable-next-line
  interface ArraySchema<T> {
    uniqueProperty(mapper: string, message?: string): ArraySchema<T>;
  }
}

yup.addMethod(yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list) {
    const errors: yup.ValidationError[] = [];

    list?.forEach((item, index: number) => {
      const propertyValue = _.get(item, propertyPath);

      if (
        propertyValue &&
        _.filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        );
      }
    });

    if (!_.isEmpty(errors)) {
      throw new yup.ValidationError(errors);
    }

    return true;
  });
});

const schema = yup
  .object({
    externalId: yup.string().required('Template Id is required'),
    attributes: yup.array().of(
      yup.object().shape({
        cat: yup.string().required('CAT is required'),
        nonCat: yup.string().required('NON-CAT is required'),
        name: yup.string().required('Attribute is required'),
        value: yup.string().required('Value is required'),
      })
    ),
    rolePriorities: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string(),
          order: yup.number(),
        })
      )
      .uniqueProperty('id', 'Must be unique'),
    durations: yup.array().of(
      yup.object().shape({
        peril: yup.string(),
        duration: yup.string().required('Must choose a duration'),
      })
    ),
    rules: yup.array().of(
      yup.object().shape({
        ifThis: yup.string().required('If statement is required'),
        operator: yup.string().required('Operator is required'),
        meta: yup.array().min(1, 'Must choose one qualifier'),
      })
    ),
    ruleDescription: yup.string().required('Must add a description'),
    targetTaskGroupId: yup
      .string()
      .required('Target Task Group Id is required'),
  })
  .required();

export const TaskTemplatesDetails = ({
  data,
  status,
  onSubmit,
  attributeDefinitions,
  companyUserRoleOptions,
  perilOptions,
  taskGroupOptions,
  onDeactivate,
  onActivate,
  permissions,
}: TaskTemplateDetailProps) => {
  const navigate = useNavigate();
  const methods = useForm<TaskTemplateForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...data,
    },
  });
  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = methods;

  React.useEffect(() => {
    if (data?.durations) {
      reset(
        {
          ...data,
        },
        { keepDirty: false }
      );
    } else {
      reset(
        { ...data, durations: [{ peril: '', duration: Duration.S }] },
        { keepDirty: false }
      );
    }
  }, [status, data, reset]);

  const sloOptions = (): { title: string; value: string }[] => {
    const slos = [];
    let count = 0;
    for (let i = count; i < 17; i++) {
      slos.push({ title: `${count}`, value: `${count}` });
      count += 15;
    }
    return [...slos];
  };

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <>
          <Grid container alignItems="center">
            <Grid item xs={8}>
              <TextField
                id="name"
                name="name"
                label="Template Name"
                control={control}
                errors={errors}
                sx={{
                  width: '100%',
                  paddingRight: '1rem',
                  margin: '0.75rem 0',
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="externalId"
                name="externalId"
                label="Template ID"
                control={control}
                errors={errors}
                sx={{ flex: 1, margin: '0.75rem 0' }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Template Description"
                control={control}
                errors={errors}
                sx={{ flex: 1, margin: '0.75rem 0' }}
                multiline
              />
            </Grid>
          </Grid>
          <SectionDivider />
          <Section title="Set Template Parameters">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  <Stack direction="row" alignItems={'baseline'}>
                    <h3 style={{ fontSize: '0.875rem' }}>
                      1. Resource Priority
                    </h3>
                  </Stack>
                </Box>
                <ResourcePriority
                  name="rolePriorities"
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  setValue={setValue}
                  options={companyUserRoleOptions}
                />
              </Grid>
            </Grid>
            <br />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h3 style={{ fontSize: '0.875rem', margin: '0' }}>
                  2. Task Duration
                </h3>
                <TaskDuration
                  options={perilOptions}
                  name="durations"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <br />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <h3 style={{ width: '100%', fontSize: '0.875rem' }}>3. SLO</h3>
              </Grid>
              <Grid item xs={4} height="100%">
                <h3 style={{ fontSize: '0.875rem' }}>4. Priority Level</h3>
              </Grid>
              <Grid item xs={4} sx={{ alignSelf: 'flex-end' }}>
                <h3 style={{ fontSize: '0.875rem' }}>5. Requirements</h3>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="start">
              <Grid item xs={4}>
                <Grid item xs={8} marginLeft="20px">
                  <SelectField
                    id={`sloInMinutes`}
                    name={`sloInMinutes`}
                    label="SLO"
                    control={control}
                    errors={errors}
                    error={
                      errors['sloInMinutes']
                        ? errors['sloInMinutes']?.message
                        : null
                    }
                    options={[...sloOptions()]}
                  />
                </Grid>
              </Grid>
              <Grid item xs={4} height="100%">
                <RadioField
                  row
                  control={control}
                  id={'priorityLevel'}
                  name={'priorityLevel'}
                  buttons={[
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' },
                  ]}
                />
              </Grid>
              <Grid item xs={4} alignItems={'flex-start'}>
                <Stack>
                  <CheckBoxField
                    sx={{
                      label: {
                        marginBottom: '0 !important',
                        marginTop: '0 !important',
                      },
                    }}
                    control={control}
                    id="isTravelRequired"
                    name="isTravelRequired"
                    label="Travel Required"
                  />
                  <CheckBoxField
                    sx={{
                      label: {
                        marginTop: '0 !important',
                      },
                    }}
                    control={control}
                    id="isStateLicenseRequired"
                    name="isStateLicenseRequired"
                    label="State License Required"
                  />
                </Stack>
              </Grid>
            </Grid>

            <br />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h3 style={{ width: '100%', fontSize: '0.875rem' }}>
                  6. Add Attributes
                </h3>
                <AttributesField
                  setValue={setValue}
                  attributeDefinitions={attributeDefinitions}
                  name="attributes"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <br />

            <Grid container spacing={2} position={'relative'}>
              <Grid item xs={12}>
                <h3 style={{ width: '100%', fontSize: '0.875rem' }}>
                  7. Set Routing Rules
                </h3>
                <RoutingRulesField
                  name="rules"
                  control={control}
                  errors={errors}
                  rulesState={getValues('rules')}
                  setValue={setValue}
                  perilOptions={perilOptions || []}
                  taskGroupOptions={taskGroupOptions || []}
                />
              </Grid>
            </Grid>
          </Section>
        </>
      );
    },
  };

  const handleDeactivation = () => {
    if (!data) return;
    onDeactivate?.(data);
  };

  const handleActivation = () => {
    if (!data) return;
    onActivate?.(data);
  };

  return (
    <FormProvider {...methods}>
      <DrawerComponent
        status={status}
        onClose={() => navigate('/admin/task-templates')}
        isActive={!data?.deactivatedAt}
        onActivate={handleActivation}
        onDeactivate={handleDeactivation}
        showActivateButton={!!data?.deactivatedAt}
        showDeactivateButton={!data?.deactivatedAt}
        onSave={() => handleSubmit(onSubmit)()}
        saveDisabled={!isDirty}
        title={data?.name || 'New Template'}
        subtitle="TASK TEMPLATE"
        showButtons={permissions && permissions.can('set', 'task-template')}
        metas={[
          <DrawerMeta title="Template #" subtitle={data && data?.externalId} />,
        ]}
        drawerWidth="75vw"
      >
        <Form<TaskTemplateForm>
          {...formArgs}
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
        />
      </DrawerComponent>
    </FormProvider>
  );
};
